import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useToast } from '@/hooks/use-toast';
import type { Database } from '@/integrations/supabase/types';

type SpotlightSettings = Database['public']['Tables']['spotlight_settings']['Row'];
type SpotlightSettingsUpdate = Database['public']['Tables']['spotlight_settings']['Update'];

async function fetchSpotlightSettings(effectiveUserId: string): Promise<SpotlightSettings | null> {
  if (!effectiveUserId) throw new Error('Not authenticated');

  // Get user's team
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('team_id')
    .eq('user_id', effectiveUserId)
    .single();

  if (profileError) throw profileError;
  if (!profile?.team_id) return null;

  // Fetch spotlight settings for this team
  const { data, error } = await supabase
    .from('spotlight_settings')
    .select('*')
    .eq('team_id', profile.team_id)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export function useSpotlightSettings() {
  const { getEffectiveUserId } = useImpersonation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['spotlight-settings'],
    queryFn: async () => {
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) throw new Error('Not authenticated');
      return fetchSpotlightSettings(effectiveUserId);
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 1,
  });

  const saveMutation = useMutation({
    mutationFn: async (updates: SpotlightSettingsUpdate) => {
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) throw new Error('Not authenticated');

      // Get user's team
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', effectiveUserId)
        .single();

      if (profileError) throw profileError;
      if (!profile?.team_id) throw new Error('No team found');

      // Upsert: create if doesn't exist, update if it does
      const { data: result, error } = await supabase
        .from('spotlight_settings')
        .upsert(
          { ...updates, team_id: profile.team_id },
          { onConflict: 'team_id' }
        )
        .select()
        .single();

      if (error) {
        if (error.code === '23505' && error.message?.includes('slug')) {
          throw new Error('This vanity URL is already taken. Please choose a different one.');
        }
        throw error;
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spotlight-settings'] });
    },
    onError: (err: Error) => {
      toast({
        title: 'Error saving settings',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['spotlight-settings'] });
  };

  return {
    settings: data ?? null,
    isLoading,
    error,
    refetch,
    invalidate,
    save: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
  };
}
